import { useEffect, useRef, useState, type RefObject } from 'react';

/**
 * Scroll + loading logic shared by the homepage 3D sections.
 *
 * The section is tall with a sticky inner panel; progress 0…1 is how far the
 * page has scrolled through it. The 3D module is imported lazily:
 *  - `eager` (a section right under the hero): after window load + idle;
 *  - otherwise: once the section is within 800px;
 *  - Save-Data / 2G-3G: only when it is on screen.
 * Never while prerendering (window.__BPC_PRERENDER__). No WebGL 2 or reduced
 * motion → mode 'static' (the component renders a still layout instead).
 */
export interface ScrollScene {
  setProgress(p: number): void;
  setActive(active: boolean): void;
  dispose(): void;
}

const prerendering = () => !!(window as unknown as { __BPC_PRERENDER__?: boolean }).__BPC_PRERENDER__;

function canUse3D() {
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return false;
  try {
    return !!document.createElement('canvas').getContext('webgl2');
  } catch {
    return false;
  }
}

export function useScrollScene<T extends ScrollScene>(opts: {
  sectionRef: RefObject<HTMLElement>;
  hostRef: RefObject<HTMLDivElement>;
  create: (host: HTMLDivElement, onFrame: () => void) => Promise<T>;
  stageStarts: number[];
  eager?: boolean;
  onFrame?: (scene: T, stage: number) => void;
}) {
  const { sectionRef, hostRef, create, stageStarts, eager = false } = opts;
  const sceneRef = useRef<T | null>(null);
  const stageRef = useRef(0);
  const onFrameRef = useRef(opts.onFrame);
  onFrameRef.current = opts.onFrame;
  const [stage, setStage] = useState(0);
  const [mode, setMode] = useState<'static' | 'pinned'>('pinned');
  const [ready, setReady] = useState(false);

  // Decided after mount, so the first client render matches the snapshot.
  useEffect(() => {
    if (!prerendering() && !canUse3D()) setMode('static');
  }, []);

  useEffect(() => {
    if (mode !== 'pinned') return;
    const section = sectionRef.current!;
    let disposed = false;
    let loading = false;
    let raf = 0;
    let visible = false;

    const progress = () => {
      const r = section.getBoundingClientRect();
      const total = r.height - window.innerHeight;
      return total > 0 ? Math.min(1, Math.max(0, -r.top / total)) : 0;
    };
    const update = () => {
      raf = 0;
      const p = progress();
      sceneRef.current?.setProgress(p);
      let i = 0;
      stageStarts.forEach((at, idx) => {
        if (p >= at) i = idx;
      });
      if (i !== stageRef.current) {
        stageRef.current = i;
        setStage(i);
      }
    };
    const onScroll = () => {
      if (!raf) raf = requestAnimationFrame(update);
    };

    const load = async () => {
      if (loading || prerendering()) return;
      loading = true;
      try {
        if (disposed || !hostRef.current) return;
        const scene = await create(hostRef.current, () => {
          if (sceneRef.current) onFrameRef.current?.(sceneRef.current, stageRef.current);
        });
        if (disposed) {
          scene.dispose();
          return;
        }
        sceneRef.current = scene;
        update();
        scene.setActive(visible);
        setReady(true);
      } catch {
        if (!disposed) setMode('static'); // no 3D after all — keep the still layout
      }
    };

    const conn = (navigator as Navigator & { connection?: { saveData?: boolean; effectiveType?: string } }).connection;
    const frugal = !!conn?.saveData || /2g|3g/.test(conn?.effectiveType || '');
    const whenIdle = () => {
      const go = () =>
        'requestIdleCallback' in window ? window.requestIdleCallback(() => load(), { timeout: 2500 }) : setTimeout(load, 1200);
      if (document.readyState === 'complete') go();
      else window.addEventListener('load', go, { once: true });
    };
    const near = new IntersectionObserver(
      ([e]) => {
        if (!e.isIntersecting) return;
        near.disconnect();
        if (frugal || !eager) load();
        else whenIdle();
      },
      { rootMargin: frugal ? '0px' : eager ? '600px 0px' : '800px 0px' },
    );
    const onScreen = new IntersectionObserver(([e]) => {
      visible = e.isIntersecting;
      sceneRef.current?.setActive(visible);
    });
    near.observe(section);
    onScreen.observe(section);
    window.addEventListener('scroll', onScroll, { passive: true });
    update();

    return () => {
      disposed = true;
      near.disconnect();
      onScreen.disconnect();
      window.removeEventListener('scroll', onScroll);
      cancelAnimationFrame(raf);
      sceneRef.current?.dispose();
      sceneRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode]);

  return { stage, mode, ready };
}
